// src/components/auth/EmailVerification.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import {
  CheckCircle,
  XCircle,
  Loader,
  Mail,
  ArrowLeft,
  Send,
  AlertCircle
} from 'lucide-react';

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');

  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [resending, setResending] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token || !email) {
        setVerifying(false);
        setSuccess(false);
        setMessageType('error');
        setMessage('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        const response = await axios.get('/api/authors/verify-email', {
          params: { token, email }
        });

        if (response.data.status === 'success') {
          setSuccess(true);
          setMessageType('success');
          setMessage(response.data.message || 'Email verified successfully!');
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/portal/login', { 
              state: { message: 'Email verified successfully! Please log in.' }
            });
          }, 3000);
        } else {
          setSuccess(false);
          setMessageType('error');
          setMessage(response.data.message || 'Verification failed. Please try again.');
          
          if (response.data.expired) {
            setExpired(true);
          }
        }
      } catch (error) {
        console.error('Verification error:', error);
        setSuccess(false);
        setMessageType('error');
        
        if (error.response) {
          setMessage(error.response.data.message || 'Verification failed. Please try again.');
          if (error.response.data.expired) {
            setExpired(true);
          }
        } else if (error.request) {
          setMessage('No response from server. Please check your internet connection.');
        } else {
          setMessage('An error occurred. Please try again.');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token, email, navigate]);

  const handleResendVerification = async () => {
    if (!email) {
      setMessageType('error');
      setMessage('Email address not found');
      return;
    }

    setResending(true);
    setMessage('');

    try {
      const response = await axios.post('/api/authors/resend-verification', { email });

      if (response.data.status === 'success') {
        setMessageType('success');
        setMessage(response.data.message || 'Verification email sent successfully!');
        setExpired(false);
      } else {
        setMessageType('error');
        setMessage(response.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend error:', error);
      setMessageType('error');
      setMessage(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#f6ecf6] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <Loader className="w-16 h-16 text-[--asfi-blue] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Verifying Your Email</h2>
          <p className="text-gray-600">Please wait while we verify your email address...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6ecf6] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-4 text-center ${
            success ? 'bg-green-600' : 'bg-red-600'
          }`}>
            {success ? (
              <CheckCircle className="w-16 h-16 text-white mx-auto mb-2" />
            ) : (
              <XCircle className="w-16 h-16 text-white mx-auto mb-2" />
            )}
            <h2 className="text-xl font-semibold text-white">
              {success ? 'Email Verified!' : 'Verification Failed'}
            </h2>
          </div>

          {/* Content */}
          <div className="p-6">
            {message && (
              <div className={`mb-4 p-4 rounded-lg flex items-center ${
                messageType === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-700' 
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {messageType === 'success' ? (
                  <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                )}
                <span className="text-sm">{message}</span>
              </div>
            )}

            {success ? (
              <div className="text-center space-y-4">
                <p className="text-gray-600">
                  Your email has been successfully verified. You will be redirected to the login page in a few seconds.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Redirecting to login...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    {expired 
                      ? 'Your verification link has expired.' 
                      : 'We could not verify your email address.'}
                  </p>
                  
                  {email && (
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <p className="text-sm text-gray-500 mb-2">Email address:</p>
                      <p className="font-medium text-[--asfi-blue]">{email}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  {expired && email && (
                    <button
                      onClick={handleResendVerification}
                      disabled={resending}
                      className="w-full px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-[--asfi-blue-dark] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {resending ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Resend Verification Email
                        </>
                      )}
                    </button>
                  )}

                  <Link
                    to="/portal/login"
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Go to Login
                  </Link>

                  <Link
                    to="/portal/signup"
                    className="w-full px-4 py-2 text-[--asfi-blue] hover:text-[--asfi-blue-dark] transition-colors text-center block"
                  >
                    Create a new account
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-4">
          Need assistance?{' '}
          <a href="mailto:support@asfirj.org" className="text-[--asfi-blue] hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;